import {
	BasicModal,
	CheckBoxCircle,
	FloatingLoader,
	NavigationBack,
	Typography,
} from "@/components";
import { GalleryItemImage, ImageEditor } from "@/components/ui";
import env from "@/env.config";
import { useBoolean } from "@/hooks/useBoolean";
import { IMAGE_EXTENSIONS, QUERY_KEYS } from "@/libraries/constant";
import { randFileName } from "@/libraries/helper";
import { getStoragesQueryOptions } from "@/queries/options/storage";
import {
	GalleriesCheckSchema,
	GalleryListSearchSchema,
	type PresignPayloadSchema,
} from "@/schemas";
import { fileService } from "@/services";
import { useStorage } from "@/stores";
import type { CreateStorageResponse, ResError, Storage } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import { useMutation, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import {
	Link,
	createFileRoute,
	retainSearchParams,
} from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import omit from "lodash/omit";
import orderBy from "lodash/orderBy";
import { useCallback, useEffect, useMemo } from "react";
import { type SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import type {
	LightboxPropsContextType,
	PluginProps,
} from "yet-another-react-lightbox";
import Lightbox, {
	addToolbarButton,
	useLightboxProps,
} from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import { getGalleries } from "./-functions/gallery";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { cn } from "@/libraries/cn";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import isEmpty from "lodash/isEmpty";
import DrawerViewSelect from "./-components/DrawerViewSelect";
import EmptyGallery from "./-components/EmptyGallery";

export const Route = createFileRoute("/dashboard/gallery/")({
	validateSearch: zodSearchValidator(GalleryListSearchSchema),
	search: {
		middlewares: [
			retainSearchParams(["view", "edit", "deleted", "export", "select"]),
		],
	},
	loader: (ctx) => getGalleries(ctx.context),
	component: RouteComponent,
});

interface DeleteEditToolbarProps {
	onDelete: (fileId: number) => Promise<void>;
}

interface DeleteEditToolbarPluginProps extends LightboxPropsContextType {
	editDelete: DeleteEditToolbarProps;
}

function DeleteEditToolbar() {
	const { editDelete } = useLightboxProps() as DeleteEditToolbarPluginProps;
	const navigate = Route.useNavigate();
	const fileId = Route.useSearch({
		select: (search) => search.view,
	});
	const onClickDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		await editDelete.onDelete(fileId);
		return navigate({
			search: () => ({
				view: undefined,
			}),
			replace: true,
		});
	};
	const onClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return navigate({
			search: () => ({
				view: undefined,
				edit: fileId,
			}),
		});
	};
	return (
		<>
			<button
				type="button"
				className="yarl__button"
				title="Delete"
				onClick={onClickDelete}
			>
				<DeleteIcon />
			</button>
			<button
				type="button"
				className="yarl__button"
				title="Edit"
				onClick={onClickEdit}
			>
				<EditIcon />
			</button>
		</>
	);
}

function DeleteEditToolbarPlugin(props: PluginProps) {
	const { augment } = props;
	// @ts-expect-error
	augment(({ toolbar, editDelete, ...restProps }) => ({
		toolbar: addToolbarButton(toolbar, "editDelete", <DeleteEditToolbar />),
		editDelete,
		...restProps,
	}));
}

function RouteComponent() {
	const navigate = Route.useNavigate();
	const messageTag = Route.useSearch({
		select: (state) => state.tag,
	});
	const tab = Route.useSearch({
		select: (s) => s.tab,
	});
	const { fileId, editFileId, isBulkDelete, isExport, isSelect } =
		Route.useSearch({
			select: (search) => ({
				fileId: search.view,
				editFileId: search.edit,
				isBulkDelete: search.deleted,
				isExport: search.export,
				isSelect: search.select,
			}),
		});
	const [refView, inView] = useInView();
	const isShowCheck = isBulkDelete || isExport || isSelect;
	const {
		value: isOpenDeleteConfirm,
		setTrue: openDeleteConfirm,
		setFalse: closeDeleteConfirm,
	} = useBoolean();
	const {
		value: isOpenDrawerViewSelect,
		setTrue: openDrawerViewSelect,
		setFalse: closeDrawerViewSelect,
	} = useBoolean();
	const {
		data: storagesRes,
		isFetchingNextPage,
		hasNextPage,
		isLoading,
		isFetching,
		fetchNextPage,
	} = useSuspenseInfiniteQuery(getStoragesQueryOptions());
	const deleteMutation = useMutation<unknown, ResError, number>({
		mutationKey: [QUERY_KEYS.MUTATE_DELETE_IMAGE],
	});
	const bulkDeleteMutation = useMutation<unknown, ResError, number[]>({
		mutationKey: [QUERY_KEYS.MUTATE_DELETE_IMAGES],
	});
	const mutateUpload = useMutation<
		CreateStorageResponse,
		ResError,
		PresignPayloadSchema[]
	>({
		mutationKey: [QUERY_KEYS.MUTATE_UPLOAD_IMAGES],
	});
	const files = useMemo(() => {
		const fileItems = storagesRes?.pages?.flatMap((page) => page?.storages);
		return orderBy(fileItems, ["updated_at"], ["desc"]);
	}, [storagesRes?.pages]);
	const { control, register, handleSubmit, getValues } =
		useForm<GalleriesCheckSchema>({
			mode: "all",
			resolver: zodResolver(GalleriesCheckSchema),
			disabled: !isShowCheck,
			defaultValues: {
				ids: [],
			},
		});
	const fileField = useFieldArray({
		control,
		name: "ids",
		keyName: "_id",
	});
	const stateStorages = useStorage((state) => state.storages);
	const setStorages = useStorage((state) => state.setStorages);
	const fileIdx = files?.findIndex((file) => file.id === fileId);
	const editFile = files?.find((file) => file.id === editFileId);
	const countCheckedFile = fileField.fields.filter((f) => f.checked).length;
	const onDelete = (id: number) => {
		return deleteMutation.mutateAsync(id, {
			onSuccess: () => {
				const fileIdx = fileField.fields.findIndex((f) => f.id === id);
				if (fileIdx < 0) return;
				fileField.remove(fileIdx);
			},
			onError: () => {
				toast.error("Failed to delete file");
			},
		});
	};
	const onCloseEdit = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		return navigate({
			// @ts-expect-error
			search: (oldSearch) => ({
				...omit(oldSearch, "edit"),
				edit: undefined,
			}),
			replace: true,
		});
	};
	const onClickEdit = () => {
		return navigate({
			search: () => ({
				edit: fileId,
				view: undefined,
			}),
			replace: true,
		});
	};
	const onDoneEdit = async (
		e: React.MouseEvent<HTMLButtonElement>,
		imgStr: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		const fileName = randFileName(IMAGE_EXTENSIONS.JPEG);
		const file = await fileService.stringToFile(imgStr, fileName);
		const uploadFiles: PresignPayloadSchema[] = [
			{
				file_name: file.name,
				file,
			},
		];
		return mutateUpload.mutate(uploadFiles, {
			onError: () => {
				toast.error("Failed to upload file");
			},
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const onFilesChange = useCallback(
		(files: Storage[]) => {
			const { fields, replace } = fileField;
			const newFileFields = files.map((file) => {
				const existFile = fields.find((f) => f.id === file.id);
				const existState = stateStorages.find((f) => f.id === file.id);
				if (existState) {
					return {
						...(existFile ?? file),
						checked: true,
					};
				}
				return {
					...(existFile ?? file),
					checked: existFile?.checked || false,
				};
			});
			return replace(newFileFields);
		},
		[fileField.fields, stateStorages],
	);
	const onHoldClick = (idx: number) => {
		if (isExport || isSelect) return;
		fileField.update(idx, {
			...fileField.fields[idx],
			checked: true,
		});
		const navTimeoutId = setTimeout(() => {
			navigate({
				search: () => ({
					deleted: true,
				}),
				replace: true,
			});
		}, 500);

		return () => {
			clearTimeout(navTimeoutId);
		};
	};
	const onSubmit: SubmitHandler<GalleriesCheckSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		const selectedStorages = data.ids.filter((f) => f.checked);
		if (!selectedStorages.length) {
			return toast.info("No file selected");
		}
		if (isBulkDelete) return openDeleteConfirm();
		if (isExport) {
			return setStorages(selectedStorages).then(() => {
				if (isExport) {
					return navigate({
						to: "/dashboard/gallery/exports",
					});
				}
				return navigate(-1);
			});
		}
		if (!isEmpty(messageTag)) {
			return openDrawerViewSelect();
		}
	};
	const onDeleteStorage = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		const fileIds = fileField.fields.filter((f) => f.checked).map((f) => f.id);
		return bulkDeleteMutation.mutate(fileIds, {
			onSuccess: () => {
				const removedFileIdxs = fileField.fields
					.map((f, idx) => ({ ...f, idx }))
					.filter((f) => f.checked)
					.map((f) => f.idx);
				fileField.remove(removedFileIdxs);
				return closeDeleteConfirm();
			},
			onError: () => {
				toast.error("Failed to delete files");
			},
		});
	};
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (files) {
			onFilesChange(files);
		}
	}, [files]);
	useEffect(() => {
		if (inView && !isFetchingNextPage && hasNextPage) {
			fetchNextPage();
		}
	}, [inView, isFetchingNextPage, hasNextPage, fetchNextPage]);
	const handleChangeTab = useCallback(
		(e: React.SyntheticEvent<Element, Event>, value: any) => {
			e.preventDefault();
			return navigate({
				replace: true,
				// @ts-expect-error
				search: (odd) => ({
					...omit(odd, ["tab"]),
					tab: value,
				}),
			});
		},
		[navigate],
	);
	return (
		<div className="relative wrapper !p-0 h-dvh bg-white mx-auto flex flex-col">
			<AppBar
				position="sticky"
				color="transparent"
				className="!bg-gradient-contact [&_*_]:text-white"
			>
				<div className="container">
					<Toolbar classes={{ root: "!p-0" }}>
						<NavigationBack />
						<Typography fontWeight={600} fontSize={14} sx={{ flexGrow: 1 }}>
							{!isExport ? "Gallery" : "Export Files"}
						</Typography>
					</Toolbar>
					<Toolbar classes={{ root: "!p-0 !items-end" }}>
						<Tabs
							value={tab}
							onChange={handleChangeTab}
							indicatorColor="primary"
							textColor="inherit"
							variant="scrollable"
							aria-label="full width tabs example"
							className="[&_.MuiTabs-indicator]:!bg-white"
						>
							<Tab
								label={`All (${storagesRes.pages?.[0]?.meta?.total_count ?? 0})`}
								value="all"
								sx={{ textTransform: "none" }}
							/>
							<Tab
								label={`Images (${0})`}
								value="images"
								sx={{ textTransform: "none" }}
							/>
							<Tab
								label={`Videos (${0})`}
								value="videos"
								sx={{ textTransform: "none" }}
							/>
						</Tabs>
					</Toolbar>
				</div>
			</AppBar>
			<div
				className={cn("relative container py-2", {
					"grid grid-cols-3 gap-2": fileField.fields.length > 0,
					"h-full bg-gray-light": !fileField.fields.length,
				})}
			>
				<FloatingLoader open={isLoading || isFetching} />
				{fileField.fields.length ? (
					<>
						{fileField.fields.map((file, fileIdx) => (
							<Link
								key={file._id}
								to="/dashboard/gallery"
								search={{ view: file.id }}
								preload={false}
								replace={true}
								disabled={isShowCheck}
								className="relative"
							>
								<CheckBoxCircle
									{...register(`ids.${fileIdx}.checked` as const, {
										onChange: (e) => {
											fileField.update(fileIdx, {
												...file,
												checked: e.target.checked,
											});
											if (countCheckedFile === 1 && !e.target.checked) {
												navigate({
													search: () => ({
														deleted: undefined,
														export: undefined,
													}),
													replace: true,
												});
											}
										},
									})}
									hidden={!isShowCheck}
									checked={file.checked}
									labelClassName="absolute z-20 top-1 left-1"
								/>
								<GalleryItemImage
									allowContextMenu
									file={file}
									data-src={file.file_id}
									onDelete={() => onDelete(file.id)}
									onEdit={onClickEdit}
									onHold={() => onHoldClick(fileIdx)}
								/>
							</Link>
						))}
						{!isFetchingNextPage && hasNextPage ? <div ref={refView} /> : null}
					</>
				) : (
					<EmptyGallery />
				)}
			</div>
			<Lightbox
				index={fileIdx}
				open={fileIdx >= 0}
				plugins={[Counter, Download, Thumbnails, DeleteEditToolbarPlugin]}
				slides={files?.map((file) => ({
					src: file.file_id,
					title: file.file_id,
				}))}
				// @ts-expect-error
				editDelete={{ onDelete }}
				close={() =>
					navigate({
						search: () => ({
							edit: undefined,
							view: undefined,
						}),
						replace: true,
					})
				}
			/>
			{editFile ? (
				<Drawer anchor="bottom" open={!!editFile} disableEnforceFocus>
					<div className="relative wrapper h-dvh w-full">
						<ImageEditor
							includeUI={{
								loadImage: {
									path: editFile.file_id,
									name: randFileName(IMAGE_EXTENSIONS.JPEG),
								},
								uiSize: {
									width: "auto",
									height: "100vh",
								},
							}}
							usageStatistics={!env.isProd}
							onDone={onDoneEdit}
							onClose={onCloseEdit}
							className="overflow-hidden"
						/>
					</div>
				</Drawer>
			) : null}
			{isShowCheck && countCheckedFile ? (
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="fixed bg-white shadow bottom-0 inset-x-0 z-20 py-3"
				>
					<div className="wrapper">
						<div className="container grid grid-cols-2 items-center">
							<Typography fontWeight={500}>
								{countCheckedFile} File selected
							</Typography>
							<Button
								type="submit"
								variant="contained"
								color={isBulkDelete ? "error" : "primary"}
								size="large"
								className="!h-12 text-sm font-semibold !shadow-none !rounded-full"
								fullWidth
							>
								{isBulkDelete ? "Delete" : "Next"}
							</Button>
						</div>
					</div>
				</form>
			) : null}
			<BasicModal isOpen={isOpenDeleteConfirm} onClose={closeDeleteConfirm}>
				<div className="flex flex-col justify-center items-center gap-3">
					<Typography fontWeight={600} fontSize={16}>
						Delete File?
					</Typography>
					<Typography align="center">
						Are you sure you wish to delete {countCheckedFile} files?
						<br />
						You cannot undo this action.
					</Typography>
					<Button
						variant="contained"
						color="error"
						size="large"
						className="!rounded-full"
						fullWidth
						onClick={onDeleteStorage}
						disabled={deleteMutation.isPending}
					>
						Delete
					</Button>
					<Button
						variant="outlined"
						color="primary"
						size="large"
						className="!rounded-full"
						fullWidth
						onClick={closeDeleteConfirm}
					>
						Cancel
					</Button>
				</div>
			</BasicModal>
			<Drawer
				open={isOpenDrawerViewSelect}
				onClose={() => closeDrawerViewSelect()}
				anchor="bottom"
				disableEnforceFocus
			>
				<DrawerViewSelect
					onClose={closeDrawerViewSelect}
					files={getValues("ids").filter((f) => f.checked)}
				/>
			</Drawer>
		</div>
	);
}
